import { useState, useEffect, useCallback } from 'react';
import {
  X,
  Keyboard,
  HelpCircle,
  Hand,
  MousePointer,
  ZoomIn,
  Move,
  ArrowLeftRight,
  Upload,
  Type,
  Layers,
  Download,
  Package,
  Undo2,
  Search,
  Pencil,
  Lightbulb,
} from 'lucide-react';
import { getShortcutsByCategory, categoryLabels } from '@/data/shortcuts';
import { gestureHints, featureTips, type GestureHint, type FeatureTip } from '@/data/mobileHelp';
import { useIsMobile } from '@/hooks/useIsMobile';
import styles from './ShortcutsPanel.module.css';
import type { ShortcutsPanelProps } from './ShortcutsPanel.types';

/** Tab types for mobile (gestures + tips) */
type MobileTab = 'gestures' | 'tips';

/** Tab types for desktop (shortcuts + tips) */
type DesktopTab = 'shortcuts' | 'tips';

/** Get icon component for gesture type */
function GestureIcon({ type, size = 20 }: { type: GestureHint['icon']; size?: number }) {
  switch (type) {
    case 'tap':
      return <MousePointer size={size} />;
    case 'long-press':
      return <Hand size={size} />;
    case 'pinch':
      return <ZoomIn size={size} />;
    case 'drag':
      return <Move size={size} />;
    case 'swipe':
      return <ArrowLeftRight size={size} />;
    case 'two-finger':
      return <Move size={size} />;
    default:
      return <Hand size={size} />;
  }
}

/** Get icon component for feature tip type */
function TipIcon({ type, size = 20 }: { type: FeatureTip['icon']; size?: number }) {
  switch (type) {
    case 'upload':
      return <Upload size={size} />;
    case 'text':
      return <Type size={size} />;
    case 'layers':
      return <Layers size={size} />;
    case 'export':
      return <Download size={size} />;
    case 'product':
      return <Package size={size} />;
    case 'undo':
      return <Undo2 size={size} />;
    case 'zoom':
      return <Search size={size} />;
    case 'draw':
      return <Pencil size={size} />;
    default:
      return <HelpCircle size={size} />;
  }
}

/** Render the tips content (shared between mobile and desktop) */
function TipsContent() {
  return (
    <div className={styles.tipList}>
      {featureTips.map((tip) => (
        <div key={tip.id} className={styles.tipItem}>
          <div className={styles.tipIcon}>
            <TipIcon type={tip.icon} size={24} />
          </div>
          <div className={styles.tipInfo}>
            <span className={styles.tipTitle}>{tip.title}</span>
            <span className={styles.tipDescription}>{tip.description}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Render keyboard shortcuts content */
function ShortcutsContent() {
  const shortcutsByCategory = getShortcutsByCategory();

  return (
    <>
      {Object.entries(shortcutsByCategory).map(([category, shortcuts]) => (
        <section key={category} className={styles.category}>
          <h3 className={styles.categoryTitle}>{categoryLabels[category] || category}</h3>
          <div className={styles.shortcutList}>
            {shortcuts.map((shortcut) => (
              <div key={shortcut.id} className={styles.shortcutItem}>
                <span className={styles.shortcutDescription}>{shortcut.description}</span>
                <kbd className={styles.shortcutKey}>{shortcut.keyLabel}</kbd>
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

export function ShortcutsPanel({ isOpen, onClose }: ShortcutsPanelProps) {
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState<MobileTab>('gestures');
  const [desktopTab, setDesktopTab] = useState<DesktopTab>('shortcuts');

  // Close on Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on overlay click
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Render desktop content with tabs
  const renderDesktopContent = () => (
    <>
      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tab} ${desktopTab === 'shortcuts' ? styles.tabActive : ''}`}
          onClick={() => setDesktopTab('shortcuts')}
          aria-selected={desktopTab === 'shortcuts'}
          role="tab"
        >
          <Keyboard size={16} aria-hidden="true" />
          Shortcuts
        </button>
        <button
          type="button"
          className={`${styles.tab} ${desktopTab === 'tips' ? styles.tabActive : ''}`}
          onClick={() => setDesktopTab('tips')}
          aria-selected={desktopTab === 'tips'}
          role="tab"
        >
          <Lightbulb size={16} aria-hidden="true" />
          Tips
        </button>
      </div>

      <div className={styles.content}>
        {desktopTab === 'shortcuts' && <ShortcutsContent />}
        {desktopTab === 'tips' && <TipsContent />}
      </div>

      <footer className={styles.footer}>
        <p className={styles.footerHint}>
          Press <kbd className={styles.footerKey}>?</kbd> to toggle this panel
        </p>
      </footer>
    </>
  );

  // Render mobile gestures and tips content
  const renderMobileContent = () => (
    <>
      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tab} ${mobileTab === 'gestures' ? styles.tabActive : ''}`}
          onClick={() => setMobileTab('gestures')}
          aria-selected={mobileTab === 'gestures'}
          role="tab"
        >
          <Hand size={16} aria-hidden="true" />
          Gestures
        </button>
        <button
          type="button"
          className={`${styles.tab} ${mobileTab === 'tips' ? styles.tabActive : ''}`}
          onClick={() => setMobileTab('tips')}
          aria-selected={mobileTab === 'tips'}
          role="tab"
        >
          <Lightbulb size={16} aria-hidden="true" />
          Tips
        </button>
      </div>

      <div className={styles.content}>
        {mobileTab === 'gestures' && (
          <div className={styles.gestureList}>
            {gestureHints.map((hint) => (
              <div key={hint.id} className={styles.gestureItem}>
                <div className={styles.gestureIcon}>
                  <GestureIcon type={hint.icon} size={24} />
                </div>
                <div className={styles.gestureInfo}>
                  <span className={styles.gestureName}>{hint.gesture}</span>
                  <span className={styles.gestureDescription}>{hint.description}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {mobileTab === 'tips' && <TipsContent />}
      </div>

      <footer className={styles.footer}>
        <p className={styles.footerHint}>
          Tap the <strong>?</strong> button anytime for help
        </p>
      </footer>
    </>
  );

  return (
    <div
      className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
      onClick={handleOverlayClick}
      aria-hidden={!isOpen}
    >
      <div
        className={`${styles.panel} ${isMobile ? styles.panelMobile : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-title"
      >
        <header className={styles.header}>
          <h2 id="shortcuts-title" className={styles.title}>
            <HelpCircle size={20} className={styles.titleIcon} aria-hidden="true" />
            Help
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close panel"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        {isMobile ? renderMobileContent() : renderDesktopContent()}
      </div>
    </div>
  );
}
